import { CommonModule } from '@angular/common';
import { Component, computed, inject, ChangeDetectionStrategy } from '@angular/core';
import { LoadingSpinnerComponent } from '@components/shared/loading-spinner/loading-spinner.component';
import type { IOverviewWidgetViewModel } from '@interfaces/overview-widget-view-model.interface';
import { WidgetType } from '@pages/reports/types/report-widget-type.enum';
import { ReportSingleService } from '@services/report-single.service';
import type { AgChartOptions } from 'ag-charts-community';
import { ReportBoxesWrapperComponent } from './boxes-wrapper/boxes-wrapper.component';
import { ChartComponent } from './widgets/chart/chart.component';
import type { ISettings } from './widgets/table/table.component';
import { TableComponent } from './widgets/table/table.component';

@Component({
    selector: 'app-overview-tab',
    standalone: true,
    imports: [CommonModule, ReportBoxesWrapperComponent, TableComponent, ChartComponent, LoadingSpinnerComponent],
    templateUrl: './overview-tab.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./overview-tab.component.scss']
})
export class OverviewTabComponent {
    private readonly service = inject(ReportSingleService);
    protected readonly widgetType = WidgetType;

    /** Per-tab spinner only after initial bootstrap (first load uses tabs-level overlay). */
    readonly isLoading = computed(() => this.service.isLoading() && this.service.initialBootstrapComplete());

    readonly boxesData = computed(() => this.service.overviewData()?.boxes ?? []);

    readonly metricsData = computed(() => this.service.overviewData()?.metrics);

    readonly chartsData = computed(() => this.service.overviewData()?.charts ?? []);

    readonly overviewLayout = computed(() => this.service.configuration()?.overviewTab?.layout ?? '');

    readonly metricsConfig = computed<ISettings>(() => {
        const widgets = this.service.configuration()?.overviewTab?.widgets ?? [];
        const metricsWidget = widgets.find((x: { type: number }) => x.type === WidgetType.Metrics);
        const metricsSettings = (
            metricsWidget as {
                settings?: {
                    cols: { name: string; label: string; format?: 'currency' | 'percent' | 'customPercent' }[];
                    rows: { name: string; label: string; format?: 'currency' | 'percent' | 'customPercent' }[];
                };
            }
        )?.settings;
        return metricsSettings ?? { cols: [], rows: [] };
    });

    readonly overviewWidgets = computed<IOverviewWidgetViewModel[]>(() => {
        const widgets = this.service.configuration()?.overviewTab?.widgets ?? [];
        let chartIndex = 0;

        return widgets.map((widget: { type: WidgetType; settings?: AgChartOptions; layoutTemplateArea?: string }) => {
            if (widget.type === WidgetType.Chart) {
                const chartData = this.chartsData()[chartIndex];
                chartIndex += 1;
                return {
                    ...widget,
                    layoutTemplateArea: widget.layoutTemplateArea ?? 'chart',
                    chartData
                } as IOverviewWidgetViewModel;
            }

            if (widget.type === WidgetType.Box) {
                return {
                    ...widget,
                    layoutTemplateArea: widget.layoutTemplateArea ?? 'boxes',
                    boxesData: this.boxesData()
                } as IOverviewWidgetViewModel;
            }

            if (widget.type === WidgetType.Metrics) {
                return {
                    ...widget,
                    layoutTemplateArea: widget.layoutTemplateArea ?? 'metrics',
                    metricsData: this.metricsData()
                } as IOverviewWidgetViewModel;
            }

            return {
                ...widget,
                layoutTemplateArea: widget.layoutTemplateArea ?? ''
            } as IOverviewWidgetViewModel;
        });
    });
}
